"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/src/lib/utils";
import type { VerdictHistoryEntry, VerdictStatus } from "./types";
import { COMMENT_MAX_LENGTH, useVerdictLabels } from "./meta";
import { TimelineRow } from "./HistoryItems";
import VerdictToggle from "./VerdictToggle";
import { ChipCommentEditor, DangerActionPicker } from "./ChipCommentEditor";
import { useDangerActions } from "./useDangerActions";
import { buildComment } from "./dangerActions";

type Tab = "view" | "compose";

interface VerdictModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Журнал заключений (новые сверху). */
  entries: VerdictHistoryEntry[];
  /** Можно ли оставлять заключение (для студента — false, виден только просмотр). */
  canVote: boolean;
  /** На каком режиме открыть. */
  initialTab: Tab;
  /** Предвыбранный статус для режима «Оставить» (когда вошли через тогл). */
  initialStatus: VerdictStatus | null;
  /** Идёт отправка. */
  submitting: boolean;
  /** Отправка заключения. Возвращает true при успехе. */
  onSubmit: (status: VerdictStatus, comment: string) => Promise<boolean>;
}

/**
 * Единая полноэкранная модалка заключений для мобилки («модуль отзывов»):
 * сегмент-контрол «Комментарии» (лента-таймлайн) / «Оставить» (тогл + редактор).
 * После успешной отправки — авто-переключение на «Комментарии».
 */
export default function VerdictModal({
  open,
  onOpenChange,
  entries,
  canVote,
  initialTab,
  initialStatus,
  submitting,
  onSubmit,
}: VerdictModalProps) {
  const labels = useVerdictLabels();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [status, setStatus] = useState<VerdictStatus | null>(initialStatus);
  const [text, setText] = useState("");
  const [chips, setChips] = useState<string[]>([]);

  const { catalog, labelOf, honestAutofill, plainList } = useDangerActions(status);

  // Сброс при открытии (правка стейта на смене пропа, без эффекта).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTab(canVote ? initialTab : "view");
      setStatus(initialStatus);
      setText("");
      setChips(initialStatus === "honest" ? honestAutofill : []);
    }
  }

  // Переключили статус внутри модалки: у школ со своим перечнем «Честно»
  // заполняется само, остальные признаки к нему не относятся.
  const selectStatus = (next: VerdictStatus) => {
    if (next === status) return;
    setStatus(next);
    setChips(next === "honest" ? honestAutofill : []);
  };

  const canSave =
    status !== null && (text.trim().length > 0 || chips.length > 0) && !submitting;

  const handleSave = async () => {
    if (!canSave || status === null) return;
    const ok = await onSubmit(status, buildComment(text, chips));
    if (ok) {
      setText("");
      setChips([]);
      setTab("view");
    }
  };

  const segBtn = (value: Tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(value)}
      className={cn(
        "rounded-md py-1.5 text-sm font-medium transition-colors",
        tab === value ? "bg-white text-foreground shadow-sm" : "text-gray-500",
      )}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="h-dvh max-w-none! top-0! left-0! translate-x-0! translate-y-0! flex! flex-col gap-0! p-0! rounded-none! border-0!"
      >
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-base font-semibold">{labels.modalTitle}</DialogTitle>
        </DialogHeader>

        {/* Сегмент-контрол — только если можно оставлять заключение. */}
        {canVote && (
          <div className="px-4 pt-3">
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1">
              {segBtn("view", labels.tabView)}
              {segBtn("compose", labels.tabCompose)}
            </div>
          </div>
        )}

        {tab === "view" ? (
          entries.length === 0 ? (
            <div className="flex flex-1 items-center justify-center px-4">
              <p className="text-sm text-gray-400">{labels.emptyHistory}</p>
            </div>
          ) : (
            <ol className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              {entries.map((entry, i) => (
                <TimelineRow
                  key={entry.id}
                  entry={entry}
                  isLast={i === entries.length - 1}
                />
              ))}
            </ol>
          )
        ) : (
          <>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
              <VerdictToggle value={status} onSelect={selectStatus} disabled={submitting} />
              <ChipCommentEditor
                text={text}
                chips={chips}
                labelOf={labelOf}
                placeholder={labels.commentPlaceholder}
                removeLabel={labels.removeTag}
                onTextChange={(v) => setText(v.slice(0, COMMENT_MAX_LENGTH))}
                onRemoveChip={(i) => setChips((prev) => prev.filter((_, idx) => idx !== i))}
              />
              <p className="text-xs text-gray-400">
                {text.length}/{COMMENT_MAX_LENGTH}
              </p>
              <DangerActionPicker
                catalog={catalog}
                selected={chips}
                disabled={submitting}
                plainList={plainList}
                onAdd={(raw) =>
                  setChips((prev) => (prev.includes(raw) ? prev : [...prev, raw]))
                }
              />
            </div>
            <div className="border-t p-4">
              <Button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                className="w-full"
              >
                {labels.save}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
